import { Button, Dialog, DialogTitle, TextField, Typography, Box, Grid2, darken } from "@mui/material";
import { Dispatch, ReactElement, ReactNode, SetStateAction, useEffect, useState } from "react";
import "./CreateTicket.css";
import CloseIcon from "@mui/icons-material/Close";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import { config } from "../../authConfig";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import { CardContainer } from "./CardContainer";
import { theme } from "../../Common/UI/styles/theme";
import SNEvent from "./interfaces/SNEvent";
import { StepsEnum } from "./HorizontalLinearStepper";
import LabeledTextField from "./components/LabeledTextField";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DescriptionIcon from "@mui/icons-material/Description";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import BusinessIcon from "@mui/icons-material/Business";
import EditedInvoice from "./interfaces/EditedInvoice";
import { useFormError } from "../../Common/UI/hooks/useFormError";
import ErrorDialog from "./ErrorDialog";
import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";

export interface SimpleDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

interface InvoiceDialogFormError {
  invoiceNumber: string;
  totalInvoice: string;
}

function SimpleDialog(props: SimpleDialogProps) {
  const { onClose, open, children, title } = props;

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>
        <Grid2 container>
          <Grid2 size={10}>
            <>{title}</>
          </Grid2>
          <Grid2 size={2} className="close-icon-container">
            <CloseIcon onClick={onClose} className="close-btn" />
          </Grid2>
        </Grid2>
      </DialogTitle>
      {children}
    </Dialog>
  );
}

const formatISODateTimeWithOffset = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // getMonth() is 0-indexed
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  const offsetMinutes = -date.getTimezoneOffset(); // Invert because getTimezoneOffset returns negative for UTC+
  const offsetSign = offsetMinutes >= 0 ? "+" : "-";
  const absOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absOffsetMinutes / 60)).padStart(2, "0");
  const offsetMins = String(absOffsetMinutes % 60).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMins}`;
};

function InvoiceDialog({
  dialogOpen,
  setDialogOpen,
  event,
  donationTotal,
  handleStep,
  setEvent,
  setEditedInvoice,
}: {
  dialogOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  event: SNEvent;
  donationTotal: number | null;
  handleStep: (stepNum: number) => void;
  setEvent: React.Dispatch<React.SetStateAction<SNEvent>>;
  setEditedInvoice: React.Dispatch<React.SetStateAction<EditedInvoice | null>>;
}): ReactElement {
  const { isViewOnly } = useIsAdminAndViewOnly();
  const execute = useFetchMsal();
  const { instance } = useMsal();

  //modal
  const [open, setOpen] = useState<boolean>(dialogOpen);

  const [name, setName] = useState("");

  const [vendorName, setVendorName] = useState<string | undefined>("");
  const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>("");
  const [totalInvoice, setTotalInvoice] = useState<string | undefined>(donationTotal?.toString());
  const [currentDate, setCurrentDate] = useState<string>(formatISODateTimeWithOffset(new Date()));
  const [headerDept, setHeaderDept] = useState<number>(9000);
  const [headerLoc, setHeaderLoc] = useState<number>(999);
  const [detailDept, setDetailDept] = useState<number>(1000);
  const [detailLoc, setDetailLoc] = useState<string | undefined>(event.location);
  const [title, setTitle] = useState<string>("Generated Invoice");

  const [createInvoiceLoading, setCreateInvoiceLoading] = useState<boolean>(false);

  const { formErrors, handleInputValidation } = useFormError<InvoiceDialogFormError>({ invoiceNumber: "", totalInvoice: "" });

  const [errorOpen, setErrorOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const activeAccount: null | AccountInfo = instance.getActiveAccount();

  const getCurrentDateFormatted = () => {
    if (!event.date) return "";

    const [year, month, day] = event.date.split("T")[0].split("-");
    return `${month}${day}${year.slice(-2)}`;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = new Date(e.target.value);
    setCurrentDate(formatISODateTimeWithOffset(selectedDate));
  };

  useEffect(() => {
    setVendorName(event.vendor?.companyName);
    setInvoiceNumber(`${event.location}-${getCurrentDateFormatted()}-SN`);

    if (event.date) {
      setCurrentDate(formatISODateTimeWithOffset(new Date(event.date)));
    }
  }, [event.vendor?.companyName, event.location, event.date]);

  useEffect(() => {
    if ((activeAccount as AccountInfo) && !!activeAccount?.name) {
      setName(activeAccount?.name);
    } else {
      setName("");
    }
  }, [activeAccount]);

  function handleCreateInvoice() {
    const payload = {
      eventId: event.id,
      invoiceNumber: invoiceNumber,
      amount: totalInvoice?.toString(),
      date: currentDate,
      user: name,
      userCurrentDate: new Date().toISOString().split("T")[0],
    };

    setCreateInvoiceLoading(true);
    setTitle("Generating Invoice");

    const url = config.rootAPIList?.generateInvoice;
    execute("POST", `${config.rootAPIUrl}${url}`, payload)
      .then((result) => {
        setEvent(result.snEvent);
        setEditedInvoice(result.invoice);
        setCreateInvoiceLoading(false);
        setDialogOpen(false);
        setErrorOpen(false);
        handleStep(StepsEnum.Invoiced);
        handleClose();
      })
      .catch((error) => {
        console.error("Unexpected error:", error);

        let errorMessage = "An unexpected error occurred.";

        try {
          if (typeof error === "string") {
            const parsedError = JSON.parse(error);
            if (parsedError?.message) {
              errorMessage = parsedError.message;
            }
          } else if (typeof error === "object" && error?.message) {
            errorMessage = error.message;
          }
        } catch (e) {
          console.error("Error parsing error message:", e);
        }

        setErrorMessage(errorMessage);
        setErrorOpen(true);
      });
  }

  const handleClose = () => {
    setErrorOpen(false);
    setDialogOpen(false);
    setOpen(false);
    setVendorName(event.vendor?.companyName);
    setInvoiceNumber(`${event.location}-${getCurrentDateFormatted()}-SN`);
    setTotalInvoice(totalInvoice);
    setCurrentDate(currentDate);
    setCreateInvoiceLoading(false);
    setTitle("Generated Invoice");
  };

  return (
    <Box>
      <SimpleDialog title={title} open={open} onClose={handleClose}>
        <LoadingIndicator show={createInvoiceLoading}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              margin: "20px"
            }}
          >
            <Grid2 container spacing={2} justifyContent={"space-between"} alignItems={"center"}>
              <Grid2 size={12}>
                <Typography sx={{ fontWeight: "500" }}>Please Review below Invoice Information:</Typography>
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={PersonIcon} label="Vendor Name" />
              </Grid2>
              <Grid2 size={7}>
                <TextField fullWidth required value={vendorName} disabled={true} onChange={(e) => setVendorName(e.target.value)} />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={DescriptionIcon} label="Invoice Number" />
              </Grid2>
              <Grid2 size={7}>
                <TextField
                  name="invoiceNumber"
                  fullWidth
                  required
                  value={invoiceNumber}
                  error={!!formErrors.invoiceNumber}
                  helperText={formErrors.invoiceNumber}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (/^\d*-?\d{0,6}-?[A-Za-z]*$/.test(value)) {
                      setInvoiceNumber(value);
                    }
                  }}
                  onBlur={(e) => {
                    const input = e.target.value.trim();
                    const invoicePattern = /^\d+-\d{6}-SN$/;

                    const errorMessage = (() => {
                      if (!invoicePattern.test(input)) {
                        return "Invalid format! Use: number-MMDDYY-SN (e.g., 8-121724-SN)";
                      }

                      const [, datePart] = input.split("-");
                      const month = parseInt(datePart.substring(0, 2), 10);
                      const day = parseInt(datePart.substring(2, 4), 10);
                      const year = parseInt("20" + datePart.substring(4, 6), 10);

                      if (month < 1 || month > 12) return "Invalid month in the date! Use a valid MMDDYY format";

                      const daysInMonth = new Date(year, month, 0).getDate();
                      if (day < 1 || day > daysInMonth) return `Invalid day for the month! Max days: ${daysInMonth}`;

                      return "";
                    })();

                    handleInputValidation(e, () => errorMessage);
                  }}
                />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={ReceiptIcon} label="Total Invoice" />
              </Grid2>
              <Grid2 size={7}>
                <TextField
                  name="totalInvoice"
                  fullWidth
                  required
                  value={totalInvoice}
                  error={!!formErrors.totalInvoice}
                  helperText={formErrors.totalInvoice}
                  disabled={false}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^(\d+\.?\d*|\.\d*)?$/.test(value)) {
                      setTotalInvoice(value === "" ? undefined : value);
                    }
                  }}
                  onBlur={(e) => {
                    handleInputValidation(e, (input) => {
                      const totalInvoicePattern = /^(?!0(\.0+)?$)(0|[1-9][0-9]*)(\.[0-9]+)?$/;
                      const value = input.trim();
                      return !totalInvoicePattern.test(value) ? "Enter a positive number" : "";
                    });
                  }}
                />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={CalendarMonthIcon} label="Invoice Date" />
              </Grid2>
              <Grid2 size={7}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  value={currentDate.split("T")[0]}
                  onChange={handleDateChange}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={BusinessIcon} label="Header Department" />
              </Grid2>
              <Grid2 size={7}>
                <TextField fullWidth required value={headerDept} disabled={true} />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={ShareLocationIcon} label="Header Location" />
              </Grid2>
              <Grid2 size={7}>
                <TextField fullWidth required value={headerLoc} disabled={true} />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={BusinessIcon} label="Detail Department" />
              </Grid2>
              <Grid2 size={7}>
                <TextField fullWidth required value={detailDept} disabled={true} />
              </Grid2>

              <Grid2 size={5}>
                <LabeledTextField LabelIcon={ShareLocationIcon} label="Detail Location" />
              </Grid2>
              <Grid2 size={7}>
                <TextField fullWidth required value={detailLoc} disabled={true} />
              </Grid2>
            </Grid2>
            {!isViewOnly &&
              <Button
                disabled={createInvoiceLoading || !!formErrors.invoiceNumber || !!formErrors.totalInvoice}
                sx={{
                  alignSelf: "flex-end",
                  mb: 1,
                  mt: 4,
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: darken(theme.palette.primary.main, 0.1),
                  },
                }}
                onClick={handleCreateInvoice}
              >
                Submit
              </Button>
            }
          </Box>
        </LoadingIndicator>
      </SimpleDialog>

      <ErrorDialog open={errorOpen} onClose={() => handleClose()} errorMessage={errorMessage} />
    </Box>
  );
}
export default InvoiceDialog;
